import React, { memo } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { Currency } from '../../../core/api/general/general.types';
import { CellLayoutType, MenuItem } from '../../../core/api/menu/menu.types';
import { MenuCardActionTypes } from '../../../core/types/menu.types';
import { formatNumberToCurrency } from '../../../utils/basketUtils';
import {
  Card,
  Image,
  PriceTag,
  QuantityControl,
  Typography,
} from '../../atoms';
import { ImageDimensionsType } from '../../atoms/Image/Image';

export interface MenuCardProps {
  actions: MenuCardActionTypes;
  basketCount: number;
  hasMenuOptions?: boolean;
  className?: string;
  currency: Currency;
  languageCode: string;
  menuItem: MenuItem;
  translate: any;
  // eslint-disable-next-line react/no-unused-prop-types
  vrId: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  menuCard: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexFlow: 'column nowrap',
    minHeight: '184px',
    position: 'relative',
    width: '100%',

    '&.menuCard--text': {
      minHeight: '145px',
    },
  },
  menuCardActive: {
    boxShadow: `inset 3px 0px 0px 0px ${theme.palette.primary.main}`,
  },

  menuItemContent: {
    display: 'flex',
    flexFlow: 'row nowrap',
    flexGrow: 2,
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 0.75, 2),

    '.menuCard--large &': {
      flexFlow: 'column-reverse nowrap',
      justifyContent: 'flex-end',
      padding: 0,
    },
  },
  menuItemText: {
    paddingTop: theme.spacing(1),
    minHeight: '112px',

    '& .name em, & .description': {
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: 'break-word',
      '-webkit-box-orient': 'vertical',
    },
    '& .name em': {
      '-webkit-line-clamp': 2,
    },
    '& .description': {
      '-webkit-line-clamp': 2,
    },

    '.menuCard--large &': {
      padding: theme.spacing(1.875),
      paddingBottom: '17px',

      '& .name em': {
        '-webkit-line-clamp': 1,
      },
    },
    '.menuCard--text &': {
      minHeight: '73px',
    },
  },
  menuItemImage: {
    boxSizing: 'content-box',
    minWidth: '112px',
    objectFit: 'cover',
    paddingLeft: theme.spacing(1.5),

    '.menuCard--large &': {
      padding: theme.spacing(1, 1, 0),

      '& img': {
        height: 'auto',
      },
    },
  },

  menuItemControls: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2, 2),
    zIndex: 3,

    '.menuCard--large &': {
      padding: theme.spacing(0.5, 2, 2),
    },
  },

  [theme.breakpoints.up('sm')]: {
    menuCard: {
      border: `1px solid ${theme.palette.grey[400]}`,

      '&:hover': {
        borderColor: theme.palette.grey[600],
        cursor: 'pointer',
      },

      '&.menuCard--text': {
        minHeight: '184px',
      },
    },

    menuCardActive: {
      borderColor: theme.palette.primary.main,
      boxShadow: `inset 0px 0px 0px 1px ${theme.palette.primary.main}`,

      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
    },

    menuItemText: {
      '.menuCard--text &': {
        minHeight: '112px',
      },
    },

    menuItemImage: {
      '.menuCard--large &': {
        padding: theme.spacing(1, 1, 0),
      },
    },
  },

  [theme.breakpoints.up('lg')]: {
    menuItemText: {
      '.menuCard--large &': {
        minHeight: '92px',
      },
    },

    '@media not all and (min-resolution:.001dpcm) { @supports (-webkit-appearance:none) and (display:flow-root)':
      {
        menuItemText: {
          '.menuCard--large &': {
            minHeight: '94px',
          },
        },
      },
  },
}));

function MenuCardComponent(props: MenuCardProps): JSX.Element {
  const {
    actions,
    basketCount,
    className = '',
    currency,
    hasMenuOptions,
    languageCode,
    menuItem,
    translate,
  } = props;
  const classes = useStyles();

  const {
    ActualPrice: actualPrice,
    CellLayoutType: cellLayoutType,
    Description: description,
    ImageUrl: imageUrl,
    Name: itemName,
  } = menuItem;

  const isActive = basketCount > 0;

  const externalMenuCardStyles = clsx('fd-menucard', className || '');
  const internalMenuCardStyles = clsx(
    classes.menuCard,
    isActive && ['fd-menucard--active', classes.menuCardActive],
    cellLayoutType === CellLayoutType.Large && 'menuCard--large',
    !imageUrl && 'menuCard--text',
  );

  const localisedPrice = formatNumberToCurrency(
    actualPrice,
    currency,
    languageCode,
  );
  const imageDimensions: ImageDimensionsType =
    cellLayoutType === CellLayoutType.Large ? [767, 460] : [112, 112];

  return (
    <Card
      className={externalMenuCardStyles}
      data-fd={`menu-card-${menuItem.MenuItemId}`}
      onClick={actions.onClickItem}
      role="listitem"
    >
      <div className={internalMenuCardStyles}>
        <div className={classes.menuItemContent}>
          <div className={classes.menuItemText}>
            <Typography className="name">
              <em>{itemName}</em>
            </Typography>
            <Typography className="description" variant="body2-secondary">
              {description}
            </Typography>
          </div>

          {imageUrl && (
            <Image
              alt={itemName}
              className={classes.menuItemImage}
              dimensions={imageDimensions}
              src={imageUrl}
            />
          )}
        </div>

        <div className={classes.menuItemControls}>
          <PriceTag
            hasMenuOptions={hasMenuOptions}
            localisedPrice={localisedPrice}
            translate={translate}
          />

          <QuantityControl
            itemName={itemName}
            onAddItem={actions.onAddItem}
            onRemoveItem={actions.onRemoveItem}
            quantity={basketCount}
            translate={translate}
          />
        </div>
      </div>
    </Card>
  );
}

const MenuCard = memo(MenuCardComponent);
MenuCard.displayName = 'MenuCard';

export { MenuCard };
